import { useEffect, useState } from 'react';
import { getPerformanceScopes } from '../api/performance';

/**
 * Which performance scopes the signed-in agent holds.
 *
 * Three places need this answer before they can decide whether to render at all —
 * the More menu row, the Home "Team this month" card and the performance screen's
 * own segment switcher — so the call is made once per session and shared. Without
 * the cache, opening More then Home then the screen would ask the server the same
 * question three times, and each answer walks the caller's downline.
 *
 * A failed probe resolves to "no scopes": the entry points stay hidden rather than
 * offering a screen that will only error.
 */
const NONE = { can: { direct: false, allGens: false, branch: false }, counts: { direct: 0, allGens: 0, branch: 0 }, branches: [] };

let cached = null; // the in-flight or settled promise

export function loadPerformanceScopes() {
  if (!cached) {
    cached = getPerformanceScopes()
      .then((r) => (r?.data ?? r) || NONE)
      .catch(() => NONE);
  }
  return cached;
}

/** Drop the cache — call on logout so the next agent does not inherit these scopes. */
export function resetPerformanceScopes() {
  cached = null;
}

export default function usePerformanceScopes() {
  const [scopes, setScopes] = useState(null);

  useEffect(() => {
    let live = true;
    loadPerformanceScopes().then((s) => { if (live) setScopes(s); });
    return () => { live = false; };
  }, []);

  const can = scopes?.can || NONE.can;
  return {
    scopes,
    ready: scopes != null,
    can,
    counts: scopes?.counts || NONE.counts,
    branches: scopes?.branches || [],
    // Anything at all to show? Gates every entry point into the module.
    any: !!(can.direct || can.allGens || can.branch),
  };
}
